import { lazy } from "react";
import { loadRemote, registerRemotes } from "@module-federation/runtime";

import { createAinaHostBridge } from "./AinaHostBridge";
import { resolveAinaExport } from "./resolveAinaExport";

/**
 * AINA ships as two webpack Module Federation remotes that BOTH publish the same
 * container global (`ainaService`, exposing `./AINAChatBot`):
 *   - @infinitylearn/aina_intel  → offline / on-device widget  (served at /aina-mf-intel)
 *   - @infinitylearn/aina_web    → cloud widget                (served at /aina-mf-web)
 *
 * The device mode is only known to the desktop app and is surfaced via
 * electronAPI.getAgentConfig().execution_mode ("LOCAL" = intel, "CLOUD" = web).
 * A plain browser has no bridge, so we default to the cloud (web) widget.
 *
 * Since both remotes share the `ainaService` container name, we can't register
 * two static remotes — instead we point `ainaService` at the chosen entry at
 * runtime and load only that one.
 */

async function resolveExecutionMode(): Promise<"LOCAL" | "CLOUD"> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const api = (window as any).electronAPI;
  if (!api?.getAgentConfig) return "CLOUD"; // browser / non-desktop
  try {
    const cfg = await api.getAgentConfig();
    return cfg?.execution_mode === "LOCAL" ? "LOCAL" : "CLOUD";
  } catch {
    return "CLOUD";
  }
}

export const AinaAppLazy = lazy(async () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const intelEntry =
    import.meta.env.VITE_AINA_INTEL_REMOTE_ENTRY ||
    `${origin}/aina-mf-intel/remoteEntry.js`;
  const webEntry =
    import.meta.env.VITE_AINA_WEB_REMOTE_ENTRY ||
    `${origin}/aina-mf-web/remoteEntry.js`;

  const mode = await resolveExecutionMode();
  const entry = mode === "LOCAL" ? intelEntry : webEntry;

  registerRemotes([{ name: "ainaService", entry }], { force: true });

  const mod = (await loadRemote("ainaService/AINAChatBot")) as Record<
    string,
    unknown
  >;
  const Root = resolveAinaExport(mod);
  return { default: createAinaHostBridge(Root) };
});
